/* Gift options in the cart.

   The one place an order becomes a gift, whatever the buyer came through. No
   gift control sits at any add-to-bag point: Stone Match knows who a piece was
   chosen for and why, the cart asks whether this order is being sent as a gift.

   Everything here is delegated from `document`, never bound to the fields
   themselves. Horizon re-renders the cart sections after any mutation, which
   replaces these nodes wholesale — bound listeners died with the old nodes and
   the checkbox went on toggling while nothing saved, which reads as the cart
   losing its mind rather than as a dead control. Delegation survives every
   re-render because it never holds a reference to what was rendered.

   Two more constraints shape the rest.

   1. cart-summary renders in both the cart page and the drawer, so more than one
      copy of this block can be live at once. All of them are re-synced after a
      write.

   2. The Stone Match wording is a private `__` cart attribute: writable, never
      readable back from Liquid or the Ajax API. So the preview is rendered from
      the sessionStorage copy while it lasts, and when it is gone the block says
      the wording is saved with the order instead of showing an empty preview.
      Turning either switch off never erases the stored wording — only the public
      flags move, so turning it back on restores the card without the session. */
(() => {
  const ROOT = window.Shopify?.routes?.root || '/';
  const SAVE_DEBOUNCE_MS = 600;

  const blocks = () => document.querySelectorAll('[data-fs-gift-options]');

  function sessionCard() {
    try {
      const context = JSON.parse(sessionStorage.getItem('fsStoneMatchGiftContext') || 'null');
      if (!context || !context.print_copy) return null;
      return { label: context.state_label || '', copy: context.print_copy };
    } catch (error) {
      return null;
    }
  }

  function setStatus(block, text) {
    const status = block?.querySelector('[data-fs-gift-status]');
    if (status) status.textContent = text;
  }

  async function saveAttributes(attributes, source) {
    try {
      const response = await fetch(`${ROOT}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ attributes }),
      });
      if (!response.ok) throw new Error(String(response.status));
      syncAll(await response.json(), source);
      setStatus(source, 'Saved');
    } catch (error) {
      // Say so rather than letting the buyer believe a note was kept. A silent
      // failure here means a card prints without the words they wrote.
      setStatus(source, 'Could not save — please try again.');
    }
  }

  // Public attributes only. The private wording is never read back and is left
  // untouched by every write here.
  function syncAll(cart, source) {
    const gift = cart?.attributes?.Gift === 'Yes';
    const note = cart?.attributes?.['Gift Note'] ?? '';
    const from = cart?.attributes?.['Gift From'] ?? '';
    blocks().forEach((block) => {
      const gate = block.querySelector('[data-fs-gift-toggle]');
      const body = block.querySelector('[data-fs-gift-body]');
      if (gate) gate.checked = gift;
      if (body) body.hidden = !gift;
      if (block === source) return;
      const noteField = block.querySelector('[data-fs-gift-note]');
      const fromField = block.querySelector('[data-fs-gift-from]');
      if (noteField && document.activeElement !== noteField) noteField.value = note;
      if (fromField && document.activeElement !== fromField) fromField.value = from;
      const count = block.querySelector('[data-fs-gift-count]');
      if (count) count.textContent = String(note.length);
      renderPreview(block);
    });
  }

  function renderPreview(block) {
    const wrapper = block.querySelector('[data-fs-gift-match]');
    if (!wrapper) return;
    const include = wrapper.querySelector('[data-fs-gift-include-match]');
    const preview = wrapper.querySelector('[data-fs-gift-preview]');
    const fallback = wrapper.querySelector('[data-fs-gift-preview-fallback]');
    const card = sessionCard();

    if (!include?.checked) {
      if (preview) preview.hidden = true;
      if (fallback) fallback.hidden = true;
      return;
    }
    if (card && preview) {
      preview.textContent = card.label ? `${card.label.toUpperCase()}\n\n${card.copy}` : card.copy;
      preview.hidden = false;
      if (fallback) fallback.hidden = true;
      return;
    }
    if (preview) preview.hidden = true;
    if (fallback) fallback.hidden = false;
  }

  const timers = new WeakMap();
  function queueSave(block) {
    window.clearTimeout(timers.get(block));
    timers.set(block, window.setTimeout(() => {
      const noteField = block.querySelector('[data-fs-gift-note]');
      const fromField = block.querySelector('[data-fs-gift-from]');
      saveAttributes({
        'Gift Note': noteField ? noteField.value : '',
        'Gift From': fromField ? fromField.value : '',
      }, block);
    }, SAVE_DEBOUNCE_MS));
  }

  document.addEventListener('input', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const block = target.closest('[data-fs-gift-options]');
    if (!block) return;
    if (target.matches('[data-fs-gift-note]')) {
      const count = block.querySelector('[data-fs-gift-count]');
      if (count) count.textContent = String(/** @type {HTMLTextAreaElement} */ (target).value.length);
      queueSave(block);
    } else if (target.matches('[data-fs-gift-from]')) {
      queueSave(block);
    }
  });

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    const block = target.closest('[data-fs-gift-options]');
    if (!block) return;

    if (target.matches('[data-fs-gift-toggle]')) {
      const body = block.querySelector('[data-fs-gift-body]');
      if (body) body.hidden = !target.checked;
      saveAttributes({ Gift: target.checked ? 'Yes' : 'No' }, block);
    } else if (target.matches('[data-fs-gift-include-match]')) {
      renderPreview(block);
      // Only the public flag moves. The stored wording stays on the cart, so
      // this is reversible even after the session copy is gone.
      saveAttributes({ 'Include Stone Match': target.checked ? 'Yes' : 'No' }, block);
    }
  });

  // A freshly rendered block arrives with its fields already correct from Liquid
  // but its preview still hidden — the preview is the one part that comes from
  // sessionStorage rather than the cart, so nothing in the markup can carry it.
  //
  // Watching for the block to appear, rather than listening for the events that
  // happen to produce it, is what makes this hold. Painting only on cart updates
  // missed the drawer entirely: the drawer renders its copy when it opens, which
  // is not a cart mutation, so the preview stayed hidden until the customer
  // toggled the switch and the change handler painted it by accident. Any future
  // path that inserts a block is covered without being enumerated here.
  const paint = () => blocks().forEach(renderPreview);
  let queued = false;
  const paintSoon = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      paint();
    });
  };

  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches?.('[data-fs-gift-options]') || node.querySelector?.('[data-fs-gift-options]')) {
          paintSoon();
          return;
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('shopify:cart:lines-update', () => window.setTimeout(paint, 400));
  document.addEventListener('DOMContentLoaded', paint);
  paint();
})();
